import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ordersAPI } from '../services/api';
import { Package, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const Orders = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      loadOrders();
    }
  }, [isAuthenticated]);

  const loadOrders = async () => {
    try {
      const data = await ordersAPI.getAll();
      setOrders(data);
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'paid':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'delivered':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { label: 'Aguardando Pagamento', class: 'bg-yellow-600' },
      paid: { label: 'Pago', class: 'bg-green-600' },
      delivered: { label: 'Entregue', class: 'bg-green-600' },
      cancelled: { label: 'Cancelado', class: 'bg-red-600' }
    };
    
    const badge = badges[status] || badges.pending;
    return <Badge className={`${badge.class} text-white`}>{badge.label}</Badge>;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-red-600 animate-spin mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white gaming-title mb-2">CARREGANDO...</h1>
          <p className="text-gray-400">Buscando seus pedidos</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-16 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 gaming-title glow-red">
            MEUS PEDIDOS
          </h1>
          <p className="text-xl text-gray-400">Acompanhe o status dos seus pedidos</p>
        </div>

        {orders.length === 0 ? (
          <div className="text-center bg-gray-900 rounded-2xl p-16 border-2 border-red-900/30">
            <Package className="w-24 h-24 text-red-600/30 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-white mb-4 gaming-title">
              NENHUM PEDIDO ENCONTRADO
            </h2>
            <p className="text-gray-400 mb-8">Você ainda não fez nenhum pedido</p>
            <a
              href="/produtos"
              className="inline-block bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-3 rounded-lg transition-all gaming-title"
            >
              VER PRODUTOS
            </a>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <Card key={order.id} className="bg-gray-900 border-2 border-red-900/30 hover:border-red-600/50 transition-all">
                <CardHeader className="border-b border-red-900/30">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <CardTitle className="text-white gaming-title flex items-center gap-3">
                        {getStatusIcon(order.status)}
                        Pedido {order.order_id}
                      </CardTitle>
                      <p className="text-gray-400 text-sm mt-1">
                        {new Date(order.created_at).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div>
                      {getStatusBadge(order.status)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-start">
                        <div>
                          <p className="text-white font-semibold">{item.product_name}</p>
                          <p className="text-gray-400 text-sm">Plano: {item.plan_type}</p>
                          <p className="text-gray-400 text-sm">Validade: {item.validity}</p>
                        </div>
                        <p className="text-red-600 font-bold gaming-title">
                          R$ {item.price.toFixed(2)}
                        </p>
                      </div>
                    ))}
                    
                    <div className="border-t border-red-900/20 pt-4 flex justify-between items-center">
                      <span className="text-white font-bold gaming-title">TOTAL</span>
                      <span className="text-red-600 font-bold gaming-title text-2xl">
                        R$ {order.total.toFixed(2)}
                      </span>
                    </div>

                    {order.status === 'pending' && (
                      <div className="bg-yellow-600/10 border border-yellow-600/30 rounded-lg p-4 mt-4">
                        <p className="text-yellow-500 text-sm">
                          ⏳ Aguardando confirmação do pagamento PIX
                        </p>
                      </div>
                    )}
                    
                    {order.status === 'delivered' && (
                      <div className="bg-green-600/10 border border-green-600/30 rounded-lg p-4 mt-4">
                        <p className="text-green-500 text-sm">
                          ✅ Pedido entregue! Verifique seu email para acessar o produto
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;
